/**
 * Centralized, production-grade WebSocket Client.
 * Manages raw WebSocket connections with auto-fallback, exponential backoff, heartbeat,
 * and seamless event dispatching.
 */

import { logger } from '@shared/logging/logger';
import { envValidator } from '@shared/config/envValidator';
import { FEATURES } from '@shared/config/features';

export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'reconnecting';

class WebSocketClient {
  private socket: WebSocket | null = null;
  private status: ConnectionStatus = 'disconnected';
  private url: string = '';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectBaseDelay = 1000; // ms
  private heartbeatIntervalMs = 25000; // standard AWS / Nginx timeout is 30-60s
  private heartbeatTimer: any = null;
  private serverTimeoutTimer: any = null;
  private reconnectTimer: any = null;
  private statusListeners = new Set<(status: ConnectionStatus) => void>();
  private messageListeners = new Set<(event: MessageEvent) => void>();

  constructor() {
    const env = envValidator.getEnv();
    if (!env.ENABLE_WEBSOCKET) {
      return;
    }
    this.url = env.WS_URL;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (this.status === 'disconnected') {
          logger.info('websocket', 'Device online, scheduling reconnection check...');
          this.reconnectAttempts = 0;
          this.connectDebounced();
        }
      });
    }
  }

  private getDefaultWsUrl(): string {
    const env = envValidator.getEnv();
    return env.WS_URL;
  }

  private connectDebounced(): void {
    const env = envValidator.getEnv();
    if (!env.ENABLE_WEBSOCKET) return;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 500);
  }

  /**
   * Initialize a connection to the WebSocket server.
   */
  public connect(): void {
    const env = envValidator.getEnv();
    if (!env.ENABLE_WEBSOCKET) {
      return;
    }

    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      logger.debug('websocket', 'Suppressing connection: offline state detected.');
      return;
    }

    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      logger.debug('websocket', 'Suppressing connection: window/tab is in the background.');
      return;
    }

    this.updateStatus(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');
    logger.debug('websocket', `Connecting to ${this.url}`);

    try {
      this.socket = new WebSocket(this.url);
      this.setupSocketEvents();
    } catch (error) {
      logger.error('websocket', 'Connection exception', error);
      this.handleReconnect();
    }
  }

  /**
   * Set up event handlers for the WebSocket connection.
   */
  private setupSocketEvents(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      logger.debug('websocket', 'Connection successfully established');
      this.status = 'connected';
      this.reconnectAttempts = 0;
      this.updateStatus('connected');
      this.startHeartbeat();
    };

    this.socket.onmessage = (event) => {
      // Handle heartbeat response
      if (this.isPongMessage(event.data)) {
        this.resetServerTimeout();
        return;
      }

      // Notify external subscribers
      this.messageListeners.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          logger.error('websocket', 'Subscriber callback failed', error);
        }
      });
    };

    this.socket.onerror = (error) => {
      logger.warn('websocket', 'Error detected', error);
    };

    this.socket.onclose = () => {
      logger.debug('websocket', 'Connection closed');
      this.stopHeartbeat();
      this.handleReconnect();
    };
  }

  /**
   * Exponential backoff reconnect strategy.
   */
  private handleReconnect(): void {
    const env = envValidator.getEnv();
    if (!env.ENABLE_WEBSOCKET) {
      return;
    }

    this.updateStatus('disconnected');
    
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      logger.debug('websocket', 'Device is offline. Connection retry aborted.');
      return;
    }

    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      logger.debug('websocket', 'Tab is hidden. Connection retry aborted.');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.warn('websocket', `Maximum reconnection attempts (${this.maxReconnectAttempts}) reached. Standing by.`);
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectBaseDelay * 2 ** this.reconnectAttempts, 30000);
    logger.debug('websocket', `Reconnecting in ${delay}ms... (Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Heartbeat (Ping/Pong) to maintain connection and identify stale sockets.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.sendPing();
    }, this.heartbeatIntervalMs);

    this.resetServerTimeout();
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.serverTimeoutTimer) clearTimeout(this.serverTimeoutTimer);
  }

  private sendPing(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'ping' }));
    }
  }

  private isPongMessage(data: string): boolean {
    try {
      const parsed = JSON.parse(data);
      return parsed.type === 'pong';
    } catch {
      return data === 'pong';
    }
  }

  private resetServerTimeout(): void {
    if (this.serverTimeoutTimer) clearTimeout(this.serverTimeoutTimer);
    
    // If the server doesn't respond in heartbeat frequency + grace period, terminate connection
    const gracePeriod = 5000;
    this.serverTimeoutTimer = setTimeout(() => {
      logger.warn('websocket', 'Server heartbeat timeout. Disconnecting socket.');
      this.disconnect();
      this.handleReconnect();
    }, this.heartbeatIntervalMs + gracePeriod);
  }

  /**
   * Closes connection and cleans up resource handles.
   */
  public disconnect(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      // Remove triggers before closing to prevent reconnection loops
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      try {
        this.socket.close();
      } catch (err) {
        // Safe ignore
      }
      this.socket = null;
    }
    this.updateStatus('disconnected');
    this.reconnectAttempts = 0;
  }

  /**
   * Send JSON payload safely.
   */
  public send(payload: object): boolean {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }

  /**
   * Get current connection status.
   */
  public getStatus(): ConnectionStatus {
    return this.status;
  }

  /**
   * Subscriptions for status changes.
   */
  public onStatusChange(callback: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(callback);
    callback(this.status);
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  /**
   * Subscription for raw messages.
   */
  public onMessage(callback: (event: MessageEvent) => void): () => void {
    this.messageListeners.add(callback);
    return () => {
      this.messageListeners.delete(callback);
    };
  }

  private updateStatus(newStatus: ConnectionStatus): void {
    this.status = newStatus;
    this.statusListeners.forEach((listener) => {
      try {
        listener(newStatus);
      } catch (err) {
        logger.error('websocket', 'Subscriber failed to update on status change', err);
      }
    });
  }
}

export const websocketClient = new WebSocketClient();
export default websocketClient;
